import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { Params, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { I18NextModule } from 'angular-i18next';
import { map, Observable } from 'rxjs';
import { AvatarDirective } from 'src/app/common/avatar/avatar.directive';
import { BaseComponent } from 'src/app/_shared/components/base/base.component';
import { IconComponent } from 'src/app/common/icon/icon.component';
import { RouteTranslatePipe } from 'src/app/_shared/pipes/route-translate.pipe';
import { User } from 'src/types/graphql';
import { HeaderSearchComponent, HeaderSearchTriggerDirective } from '../header/header-search/header-search.component';
import { UserService } from '../../../common/user/user.service';
import { SearchResultsFilterablesService } from '../../../../../utils/search-results-filterable';
import { ParamsService } from '../../../pages/search-results/params/params.service';
import { RouteTranslateService } from '../../../common/route-translate/route-translate.service';

@Component({
  selector: 'sharry-bottom-bar',
  templateUrl: './bottom-bar.component.html',
  styleUrls: ['./bottom-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    RouteTranslatePipe,
    I18NextModule,
    RouterLink,
    AvatarDirective,
    IconComponent,
    HeaderSearchTriggerDirective,
    HeaderSearchComponent,
    MatButtonModule,
    AsyncPipe,
    RouterLinkActive,
  ],
})
export class BottomBarComponent extends BaseComponent {
  bottomBarButtons$: Observable<{
    user: Partial<User>;
    list: BottomBarButton[];
  }>;
  activeList: boolean[] = [];
  canClearFilter = signal<boolean>(false);

  constructor(
    private userService: UserService,
    private searchResultsFilterablesService: SearchResultsFilterablesService,
    private paramService: ParamsService,
    private router: Router,
    private routeTranslateService: RouteTranslateService,
  ) {
    super();
    this.bottomBarButtons$ = this.userService.user$.pipe(
      map(user => {
        const buttonList: BottomBarButton[] = [
          {
            text: 'explore.bottom-bar.explore',
            icon: 'house',
            link: 'explore',
            class: 'sharry-small',
            color: 'secondary',
          },
          {
            text: 'explore.bottom-bar.search',
            icon: 'magnifying-glass',
            link: null,
            class: 'sharry-small',
            color: 'secondary',
          },
          {
            text: 'header.likes-short',
            icon: 'preferiti-mobile',
            link: 'likes',
            class: 'sharry-small',
            color: 'secondary',
          },
          {
            text: 'explore.bottom-bar.community',
            icon: 'ico-community',
            link: 'community',
            class: 'sharry-small',
            color: 'secondary',
          },
          {
            text: 'explore.bottom-bar.shop',
            icon: 'zaino',
            link: null,
            class: 'sharry-small',
            color: 'secondary',
            onClick: () => this.onNavigateTo(),
          },
        ];
        if (!user) {
          buttonList.push({
            text: 'explore.bottom-bar.sign-up',
            icon: 'user-circle',
            link: [],
            class: 'sharry-small',
            color: 'secondary',
            queryParams: { signup: true },
          });
        } else {
          buttonList.push({
            text: '',
            icon: '',
            link: 'profile/home',
            class: 'sharry-big',
            color: 'secondary',
            avatar: true,
          });
        }
        return { user, list: buttonList };
      }),
    );
  }

  onActiveChange(index: number) {
    this.activeList = this.activeList.map(() => false);
    this.activeList[index] = true;
    this.canClearFilter.set([0, 2, 4, 5].includes(index));
  }

  onNavigateTo() {
    const queryParams = {
      types: [
        ...this.searchResultsFilterablesService.experienceFilterableIds,
        ...this.searchResultsFilterablesService.tourFilterableIds,
        ...this.searchResultsFilterablesService.itemFilterableIds,
      ],
    };
    this.paramService.setKindsFromUrlTypes(queryParams?.types);
    this.router.navigate([this.routeTranslateService.translateRoute('search-results')], {
      queryParams,
    });
  }
}

export interface BottomBarButton {
  text: string;
  icon: string;
  link: string | string[];
  class: string;
  color: string;
  queryParams?: Params;
  avatar?: boolean;
  onClick?: () => void;
}
